// Api route
// Utils
import { post as apiPost, put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import {
    SCHEDULER_POST,
    SCHEDULER_POSTS,
    SCHEDULER_SEND_NOTIFICATION
} from '@/lib/routes/apiEndpoints';

const useSubmitPost = ({ forcedSocialProfile = null } = {}) => {
    const clearPost = (post, crossPostProfiles) => {
        // remove data from post if not autopost
        if (!post?.autopost) {
            delete post?.data?.tags;
            delete post?.data?.location;
        }
        return {
            ...post,
            cross_post_id: !crossPostProfiles?.length ? null : post.cross_post_id
        };
    };

    const sendNotificationRequest = async (scheduler_post_id) => {
        await apiPost(SCHEDULER_SEND_NOTIFICATION, { scheduler_post_id });
    };

    const submitSinglePost = async (
        dataPost,
        socialProfileId,
        callbackSuccess = () => {
            /** */
        },
        callbackError = () => {
            /** */
        },
        dry_mode = true,
        updateCrossPosts = undefined
    ) => {
        try {
            if (dataPost?.id) {
                // Edit a post
                const { data } = await put(
                    injectEndpointUrlParams(SCHEDULER_POST, {
                        SPID: socialProfileId,
                        postId: dataPost?.id
                    }),
                    {
                        ...dataPost,
                        dry_mode: dry_mode,
                        update_cross_posts: updateCrossPosts
                    }
                );
                if (!dry_mode) {
                    callbackSuccess(data.post, socialProfileId);
                }
                return data;
            }

            // Create a post
            const { data } = await apiPost(
                injectEndpointUrlParams(SCHEDULER_POSTS, {
                    SPID: socialProfileId
                }),
                { ...dataPost, dry_mode: dry_mode }
            );
            if (!dry_mode) {
                callbackSuccess(data?.post, socialProfileId);
            }
            return {
                status: 200
            };
        } catch (e) {
            let errorMessage = e.message;

            if (e?.errors?.['data.title']) {
                errorMessage = 'tiktok_carousel_title_required';
            } else if (e?.errors?.autopost?.[0]) {
                errorMessage = e?.errors?.autopost?.[0];
            }

            // return autopost error if ther is one else return default error message
            callbackError(errorMessage, socialProfileId);

            //TODO Remove after prod migration
            if (e?.message?.error === 'migration-upgrade-area-error') {
                return {
                    status: 422,
                    error: 'migration-upgrade-area-error'
                };
            } else {
                return {
                    status: 422
                };
            }
        }
    };

    const submitPosts = async (
        post,
        crossPostProfiles = [],
        callbackGlobalSuccess = () => {
            /** */
        },
        callbackPostSuccess = () => {
            /** */
        },
        callbackGlobalError = () => {
            /** */
        },
        callbackPostError = () => {
            /** */
        },
        dry_mode = true
    ) => {
        const crossPostProfilesToSubmit = crossPostProfiles?.length
            ? crossPostProfiles.filter((profile) => profile.cross_post_status.selected)
            : [];
        const requests = [
            async () =>
                await submitSinglePost(
                    clearPost(post, crossPostProfilesToSubmit),
                    forcedSocialProfile?.id,
                    callbackPostSuccess,
                    callbackPostError,
                    dry_mode
                )
        ];

        crossPostProfilesToSubmit.map((profile) => {
            if (profile.cross_post_status.selected) {
                requests.push(
                    async () =>
                        await submitSinglePost(
                            profile?.post,
                            profile?.id,
                            callbackPostSuccess,
                            callbackPostError,
                            dry_mode
                        )
                );
            }
        });

        const results = await Promise.all(requests.map((req) => req()));
        const hasError = results.find((promise) => promise.status === 422);
        if (!hasError) {
            if (dry_mode) {
                submitPosts(
                    post,
                    crossPostProfiles,
                    callbackGlobalSuccess,
                    callbackPostSuccess,
                    callbackGlobalError,
                    callbackPostError,
                    false
                );
            } else {
                callbackGlobalSuccess();
            }
        } else {
            // pass error to callback
            callbackGlobalError(hasError);
        }
    };

    return { submitPosts, submitSinglePost, sendNotificationRequest };
};

export default useSubmitPost;
